// Miami MVP — Spanish configuration (served at /es/miami).
// Mirrors miamiConfig.js; industry ids stay identical so icons/analytics align.

export const PEOPLE_BUCKETS_ES = ["1–5", "6–10", "11–25", "25+"];

export const CAPACITY_QUESTIONS_ES = [
  { id: "searching", label: "Buscar información" },
  { id: "reentering", label: "Ingresar la misma información dos veces" },
  { id: "chasing", label: "Perseguir actualizaciones" },
  { id: "errors", label: "Corregir errores" },
  { id: "moving", label: "Mover información entre departamentos" },
];

export const CAPACITY_EXAMPLE_ES = [
  { role: "Ventas", hours: 8 },
  { role: "Operaciones", hours: 12 },
  { role: "Contabilidad", hours: 6 },
  { role: "Gerencia", hours: 4 },
];

export const INDUSTRIES_ES = [
  {
    id: "construction",
    label: "Construcción",
    process: "Del proyecto al pago",
    roles: ["Estimador", "Gerente de proyecto", "Obra", "Contabilidad", "Dueño"],
    headline: "Del proyecto al pago, sin perseguir a nadie.",
    message: "Todos trabajan desde el mismo expediente del proyecto, pero cada persona recibe exactamente lo que necesita.",
    info: ["cliente", "contrato", "planos", "RFI", "submittals", "cronograma", "órdenes de cambio", "mano de obra", "materiales", "avance", "facturas"],
    pains: [
      "Los RFI esperan en los buzones",
      "Las órdenes de cambio se cotizan dos veces",
      "Las facturas esperan confirmaciones de la obra",
      "El estado vive en llamadas telefónicas",
    ],
    cta: "Encuentre mi cuello de botella",
  },
  {
    id: "hvac",
    label: "HVAC",
    process: "Del contacto al servicio",
    roles: ["Ventas", "Despachador", "Técnico", "Cliente", "Contabilidad"],
    headline: "Del contacto a la visita de servicio, sin reescribir nada.",
    message: "Todos trabajan desde el mismo expediente de cliente/trabajo, sin darle a cada rol información que no necesita.",
    info: ["contacto", "cliente", "equipo", "cotización", "cita", "orden de trabajo", "técnico", "repuestos", "factura", "pago"],
    pains: [
      "Los técnicos llaman a la oficina por los detalles del trabajo",
      "Las cotizaciones se estancan después de la visita",
      "Los repuestos se vuelven a pedir por teléfono",
      "Las facturas esperan órdenes de trabajo en papel",
    ],
    cta: "Encuentre mi cuello de botella",
  },
  {
    id: "property-management",
    label: "Administración de propiedades",
    process: "Del inquilino a la solución",
    roles: ["Inquilino", "Administrador", "Mantenimiento", "Proveedor", "Contabilidad"],
    headline: "De la solicitud del inquilino a la solución, en un solo expediente.",
    message: "Una sola fuente de verdad. Espacios de trabajo distintos para roles distintos.",
    info: ["inquilino", "propiedad", "unidad", "solicitud", "mantenimiento", "proveedor", "orden de trabajo", "costo", "factura", "solución"],
    pains: [
      "Las solicitudes llegan por texto, correo y teléfono",
      "Los inquilinos persiguen actualizaciones",
      "Los costos de proveedores se concilian a fin de mes",
      "Nadie sabe qué sigue abierto",
    ],
    cta: "Encuentre mi cuello de botella",
  },
  {
    id: "plumbing-field-service",
    label: "Plomería / Servicio en campo",
    process: "De la llamada al cobro",
    roles: ["Contacto", "Despacho", "Técnico", "Trabajo", "Factura", "Pago"],
    headline: "De la llamada al cobro, sin la pizarra.",
    message: "Un solo expediente desde la primera llamada hasta el pago final — nada se vuelve a ingresar en el camino.",
    info: ["contacto", "programación", "despacho", "información del técnico", "notas del trabajo", "repuestos", "fotos", "comunicación con el cliente", "facturación", "seguimiento de pago"],
    pains: [
      "El horario es una pizarra",
      "Los técnicos envían fotos del trabajo por texto a la oficina",
      "Las facturas salen días después del trabajo",
      "El seguimiento de pagos depende de la memoria",
    ],
    cta: "Encuentre mi cuello de botella",
  },
  {
    id: "other",
    label: "Otro negocio de servicios",
    process: "De la solicitud a la factura",
    roles: ["Recepción", "Ejecución", "Revisión", "Contabilidad", "Dueño"],
    headline: "Su proceso, mapeado una vez — y fluyendo.",
    message: "La misma idea, adaptada a su negocio: un solo expediente, roles claros, sin persecuciones entre ellos.",
    info: ["solicitud", "alcance", "cronograma", "trabajo", "revisión", "factura", "pago"],
    pains: [
      "Todo pasa por una sola persona",
      "El estado vive en los buzones",
      "La facturación espera a fin de mes",
      "Los mismos datos se ingresan dos veces",
    ],
    cta: "Encuentre mi cuello de botella",
  },
];

export const getIndustryEs = (id) => INDUSTRIES_ES.find((i) => i.id === id) || null;

export const MIAMI_ES = {
  docTitle: "OceanData Miami — Devuélvale a su equipo el tiempo que pasa administrando el trabajo",
  eyebrow: "OceanData · Miami-Dade",
  heroLine1: "Devuélvale a su equipo el tiempo",
  heroLine2: "que pasa administrando el trabajo.",
  heroSub:
    "OceanData conecta a su gente, su información y sus procesos para que el trabajo avance de un rol al siguiente sin fricción, duplicación ni persecuciones innecesarias.",
  ctaBottleneck: "Encuentre mi cuello de botella",
  ctaCalculate: "Calcule mi capacidad",
  stepOneEyebrow: "Paso uno",
  selectorTitle: "Un proceso. Una industria. Una sola fuente de verdad.",
  selectorSub: "¿Qué tipo de negocio tiene?",
  oneRecordCarries: "Un solo expediente lleva",
  flowCtaHint: "Luego vea cuánto valen esas horas, más abajo.",
  flowEmpty: "Elija su industria arriba — le mostraremos el proceso exacto que podemos simplificar primero.",
  howEyebrow: "Cómo trabaja OceanData",
  howTitle: "Cinco pasos. Un solo flujo.",
  steps: [
    "Elegir el proceso",
    "Mapear los roles",
    "Centralizar la información",
    "Automatizar los traspasos",
    "Medir la capacidad recuperada",
  ],
  roiEyebrow: "El ROI es capacidad",
  roiTitle: "Lo que un solo proceso simplificado le devolvió a un equipo.",
  perWeek: "por semana",
  roiTotal: "= 30 horas a la semana, recuperadas.",
  roiCta: "Calcule su capacidad",
  calcEyebrow: "Calculadora de capacidad",
  calcTitle: "Dos minutos. Aritmética honesta.",
  calcSub: "Estime sin preocupación — los rangos sirven. Es un punto de partida para conversar, no una auditoría.",
  peopleQ: "¿Cuántas personas participan en este proceso?",
  hoursQ: "¿Cuántas horas por semana se pierden en:",
  hoursHint: "Entre todos los involucrados, por semana.",
  rateLabel: "Opcional: costo por hora cargado",
  calcRun: "Calcular mi capacidad",
  resultEyebrow: "Oportunidad de capacidad estimada",
  perYear: "al año (× 52 semanas)",
  valueSuffix: "de capacidad al año",
  disclaimer:
    "Estimación ilustrativa: horas perdidas por semana × 52 semanas × su costo por hora cargado. Una estimación con supuestos explícitos, no un resultado medido — la auditoría gratuita la reemplaza con cifras de su propia semana.",
  formEyebrow: "Revisión de capacidad gratuita",
  formTitle: "Encontremos adónde se está yendo su capacidad.",
  formSub:
    "Quince minutos con una persona que sabe dónde se esconden las horas. Sin discurso de venta, sin secuencias — respuesta en un día hábil.",
  successTitle: "Ya está sobre nuestro escritorio.",
  successBody:
    "Una persona — normalmente Bruno — le responderá en un día hábil con dos o tres preguntas sobre adónde va el tiempo de su equipo.",
  industryPh: "Industria",
  bottleneckPh: "Mayor cuello de botella del proceso",
  fallbackPain: "Donde sea que desaparezcan las horas",
  peoplePh: "Personas involucradas",
  weeklyHoursPh: "Horas semanales perdidas (est.)",
  namePh: "Nombre",
  companyPh: "Empresa",
  emailPh: "Correo de trabajo",
  phonePh: "Teléfono (opcional)",
  submitIdle: "Muéstreme dónde se pierde la capacidad",
  submitSending: "Enviando…",
  privacy: "Solo usamos esto para responderle. Nada más.",
  finalTitle: "¿Cuánta capacidad podríamos devolverle a su equipo?",
  finalSub:
    "No vendemos software. Le devolvemos capacidad a su equipo arreglando cómo se mueve el trabajo dentro del negocio.",
  finalCta: "Descúbralo",
  toastCalcError: "Elija el tamaño del equipo e ingrese al menos una estimación de horas semanales.",
  toastSuccess: "Recibido — una persona le responderá en un día hábil.",
  toastError: "Algo no salió bien. Inténtelo de nuevo, por favor.",
  relatedLabel: "Guías de software",
  relatedLinks: [
    { label: "Zoho FSM para servicio de campo", href: "/es/zoho-fsm" },
    { label: "Integraciones contables de Zoho FSM", href: "/es/zoho-fsm-accounting" },
    { label: "Zoho Projects para construcción", href: "/es/zoho-projects-construction" },
    { label: "Edificios conectados con Zoho IoT", href: "/es/zoho-iot-connected-buildings" },
    { label: "Zoho FSM vs Zuper", href: "/es/zoho-fsm-vs-zuper" },
  ],
};
